import {
  memo,
  useRef,
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react'
import { useMobile } from 'src/hooks/useMobile'
import { sendProductInformationSelectedEvent } from 'src/utils/amplitude/sendProductInformationSelectedEvent'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { ProductContext } from 'src/components/restructure/product/contexts'
import ArrowRightSliderIcon from 'src/components/Icons/ArrowRightSliderIcon'

import CarouselNavButtons from './CarouselNavButtons'

import './styles.scss'

interface GenericCarouselProps {
  title?: string
  children: React.ReactNode
  variant?: 'default' | 'inline' // Controla se os botões são no topo ou na lateral
  arrowLeftActive?: boolean
  onNavigate?: (direction: 'prev' | 'next') => void
}

const GenericCarousel = ({
  title,
  children,
  variant = 'default',
  arrowLeftActive = false,
  onNavigate,
}: GenericCarouselProps) => {
  const listRef = useRef<HTMLDivElement | null>(null)
  const [navState, setNavState] = useState<{ prev: boolean; next: boolean }>({
    prev: false,
    next: true,
  })

  const [thumbWidth, setThumbWidth] = useState(20)
  const [hasScroll, setHasScroll] = useState(true)
  const thumbRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)
  const startXRef = useRef<number>(0)
  const initialScrollLeftRef = useRef<number>(0)
  const { isMobile } = useMobile()
  const { currentSku } = useContext(ProductContext)
  const [carouselScrollLeft, setCarouselScrollLeft] = useState(0)
  const hasFiredEventRef = useRef(false)

  const SPEED_MULTIPLIER = -3
  const EXTRA_WIDTH = 152
  const INLINE_SCROLL_THRESHOLD = 5
  const DEFAULT_SCROLL_THRESHOLD = 210

  const isInline = variant === 'inline'

  const featureDetailsObject = getFeatureObject({
    locationOnPage: null,
    name: 'Caracteristicas',
    section: 'produto',
  })

  const { pageData } = useGlobalContext()

  const standardObject = getStandardObject(pageData)
  const productObject = getProductQueryObject({
    ...currentSku,
  })

  const getCarouselWidth = useCallback(() => {
    if (!listRef.current) return 0

    return listRef.current.getBoundingClientRect().width + EXTRA_WIDTH
  }, [EXTRA_WIDTH])

  const handleThumbMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current || !thumbRef.current || !listRef.current) {
        return
      }

      const deltaX = e.clientX - startXRef.current
      const wrapper = listRef.current
      const scrollWidth = wrapper.scrollWidth - wrapper.offsetWidth
      const newScrollLeft = Math.min(
        Math.max(initialScrollLeftRef.current - deltaX * SPEED_MULTIPLIER, 0),
        scrollWidth
      )

      wrapper.scrollLeft = newScrollLeft
    },
    [SPEED_MULTIPLIER]
  )

  const handleThumbMouseUp = useCallback(() => {
    isDraggingRef.current = false
    setIsDragging(false)
    document.removeEventListener('mousemove', handleThumbMouseMove)
    document.removeEventListener('mouseup', handleThumbMouseUp)
  }, [handleThumbMouseMove])

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!thumbRef.current || !listRef.current) return
    setIsDragging(true)
    isDraggingRef.current = true
    startXRef.current = e.clientX
    initialScrollLeftRef.current = listRef.current.scrollLeft
    document.addEventListener('mousemove', handleThumbMouseMove)
    document.addEventListener('mouseup', handleThumbMouseUp)
  }

  const onScroll = useCallback(() => {
    if (!listRef.current) return
    const wrapperWidth = Math.ceil(
      listRef.current.getBoundingClientRect().width
    )

    const { scrollWidth, scrollLeft } = listRef.current
    const scrollThreshold = isInline
      ? INLINE_SCROLL_THRESHOLD
      : DEFAULT_SCROLL_THRESHOLD

    const scrollable = scrollWidth - wrapperWidth > scrollThreshold

    setHasScroll(scrollable)

    setNavState({
      prev: scrollLeft !== 0,
      next: scrollWidth - wrapperWidth - scrollLeft > 5,
    })
    setThumbWidth((wrapperWidth / scrollWidth) * 100)
  }, [isInline])

  const moveTo = (direction: 'prev' | 'next') => {
    if (!listRef.current) return
    const scrollAmount = !isMobile
      ? getCarouselWidth()
      : listRef.current.offsetWidth

    listRef.current.scrollLeft +=
      direction === 'next' ? scrollAmount : -scrollAmount
  }

  const onPrevClick = () => {
    moveTo('prev')
    onNavigate?.('prev')
  }

  const onNextClick = () => {
    moveTo('next')
    onNavigate?.('next')
  }

  useEffect(() => {
    setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollLeft = 0
        onScroll()
      }
    }, 100)
    const el = listRef.current

    if (!el) return

    const handleScroll = () => {
      onScroll()
      setCarouselScrollLeft(el.scrollLeft)
    }

    el.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', onScroll)

    return () => {
      el.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [onScroll, children])

  useEffect(() => {
    if (carouselScrollLeft === 0) return
    if (!hasFiredEventRef.current) {
      sendProductInformationSelectedEvent({
        featureDetailsObject,
        standardObject,
        productObject,
        clickedElement: 'Caracteristicas',
      })
      hasFiredEventRef.current = true
    }
  }, [carouselScrollLeft, featureDetailsObject, productObject, standardObject])

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleThumbMouseMove)
      document.removeEventListener('mouseup', handleThumbMouseUp)
    }
  }, [handleThumbMouseMove, handleThumbMouseUp])

  useEffect(() => {
    if (!listRef.current) return
    const wrapper = listRef.current

    wrapper.style.scrollSnapType = isDragging ? 'none' : 'x mandatory'
    wrapper.style.scrollBehavior = isDragging ? 'unset' : 'smooth'
  }, [isDragging])

  const shouldShowInlineNextButton =
    isInline && !isMobile && hasScroll && navState.next

  const shouldShowInlinePrevButton =
    arrowLeftActive && !isMobile && hasScroll && navState.prev

  return (
    <>
      <div
        className={`GenericCarouselContainer md:max-w-full ${
          isInline ? 'relative' : 'pl-5 xl:pl-0'
        }`}
      >
        {!isInline && (
          <div className="flex flex-row justify-between items-center max-w-[1648px]">
            <p className="text-restructure-primary text-2xl font-medium font-inter">
              {title}
            </p>
            {!isMobile && hasScroll && (
              <CarouselNavButtons {...{ onNextClick, onPrevClick, navState }} />
            )}
          </div>
        )}

        <div className={isInline ? 'relative flex items-center w-full' : ''}>
          {shouldShowInlinePrevButton && (
            <button
              type="button"
              onClick={onPrevClick}
              className="hidden md:flex absolute left-0 top-0 bottom-10 w-7 bg-neutral02 items-center justify-center z-10 transition-colors cursor-pointer after:content-[''] after:absolute after:left-full after:top-0 after:bottom-0 after:w-10 after:bg-gradient-to-l after:from-transparent after:to-white"
              aria-label="Benefícios anteriores"
            >
              <span className="inline-flex rotate-180">
                <ArrowRightSliderIcon />
              </span>
            </button>
          )}

          <div
            ref={listRef}
            className="generic-carousel flex flex-nowrap gap-6 overflow-x-auto pb-10 relative"
            onScroll={onScroll}
          >
            {children}
          </div>

          {shouldShowInlineNextButton && (
            <button
              type="button"
              onClick={onNextClick}
              className="hidden md:flex absolute right-0 top-0 bottom-10 w-7 bg-neutral02 items-center justify-center z-10 transition-colors cursor-pointer before:content-[''] before:absolute before:right-full before:top-0 before:bottom-0 before:w-10 before:bg-gradient-to-r before:from-transparent before:to-white"
              aria-label="Próximos benefícios"
            >
              <ArrowRightSliderIcon />
            </button>
          )}
        </div>

        {hasScroll && (
          <div
            className={`generic-carousel-scrollbar ${
              thumbWidth === 100 ? 'hidden' : ''
            } pr-1 md:pr-0`}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div
              className={`generic-carousel-scrollbar-track w-[${thumbWidth}%] max-w-full`}
            />
            <div
              onMouseDown={handleThumbMouseDown}
              style={{
                left: listRef.current
                  ? `${
                      (listRef.current.scrollLeft /
                        listRef.current.scrollWidth) *
                      100
                    }%`
                  : '0%',
                width: `${thumbWidth}%`,
              }}
              ref={thumbRef}
              className="generic-carousel-scrollbar-thumb"
              tabIndex={0}
              role="slider"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={
                listRef.current
                  ? (listRef.current.scrollLeft / listRef.current.scrollWidth) *
                    100
                  : 0
              }
              aria-label="scrollbar thumb"
            />
          </div>
        )}
      </div>
    </>
  )
}

export default memo(GenericCarousel)
